import { onViewLoad } from "~/jose_modern/on_view_load";
import { Highlighter } from "~/jose_modern/highlighter";
import { initBillingHistory, fetchBillingHistory } from "./billing";

// We'll store the last section that was highlighted so we can
// see if the user is leaving "subscription-cancel".
let lastHighlighted = null;

function getQueryParam(param) {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get(param);
}

function updateURLWithParam(param, value) {
  const url = new URL(window.location);
  url.searchParams.set(param, value);
  url.hash = "";
  return url;
}

function setupSectionNavigation() {
  const DEFAULT_SECTION = "email-settings";

  /// Set up settings section navigation. This depends on the sidebar linking to ?section,
  /// as well as the involved controller actions handling params[:section]. Nitty reminder:
  /// in cases where the controller action is called by a form submission, you should add a
  /// `hidden_field_tag` defining `section`, rather than adding it to the path params.
  const sections = document.querySelectorAll(".settings-section");
  const sidebarMenu = document.getElementById("sidebar-menu");
  const settingsPage = document.querySelector(".user-settings-page");

  function revealSection(section) {
    sections.forEach((sec) => (sec.style.display = "none"));
    const activeSection = document.getElementById(section);
    if (activeSection) {
      activeSection.style.display = "block";
    }
    return activeSection;
  }

  function scrollToHash(activeSection) {
    const id = decodeURIComponent(window.location.hash.slice(1));
    const target = id && document.getElementById(id);
    if (!target || !activeSection?.contains(target)) return;

    requestAnimationFrame(() => target.scrollIntoView({ block: "center" }));
  }

  function updateURL(section) {
    const url = updateURLWithParam("section", section);
    history.pushState(null, "", url.toString());
  }

  function updateSidebarActiveLink(section) {
    document.querySelectorAll(".menu-list a").forEach((link) => link.classList.remove("is-active"));
    const activeLink = document.querySelector(`a[href="?section=${section}"]`);
    if (activeLink) {
      activeLink.classList.add("is-active");
    }
  }

  function maybeActivateLinkedAccountsTab(section) {
    try {
      const linkedAccountsTabLink = document.getElementById("linked-accounts-tab-link");
      const linkedAccountsTab = linkedAccountsTabLink?.parentElement;
      if (linkedAccountsTab) {
        if (section === "linked-accounts") {
          linkedAccountsTab.classList.add("is-active");
        } else {
          linkedAccountsTab.classList.remove("is-active");
        }
      }
    } catch (err) {
      console.error(err);
    }
  }

  function maybeScrollIntoView(currentSection, previousSection) {
    if (currentSection === "subscription-cancel") {
      const container = document.getElementById("cancel-plan-container");
      if (container) {
        container.scrollIntoView({ behavior: "smooth" });
        Highlighter.highlight(container);
      }
    } else if (previousSection && currentSection !== previousSection) {
      const navbar = document.getElementById("navbar");
      navbar?.scrollIntoView({ behavior: "smooth" });
    }
  }

  /// `resolveSection` handle any cases of aliasing sections; for example,
  /// "subscription-cancel" is just a subsection within "subscriptions."
  /// Returns `[actualSection, highlightSection]`, where `actualSection` is the DOM
  /// element id that gets shown/hidden, and `highlightSection` is what we highlight in the nav.
  function resolveSection(section) {
    const highlightSection = section;
    let actualSection = section;
    if (section === "subscription-cancel") {
      actualSection = "subscriptions";
    }
    return [actualSection, highlightSection];
  }

  function updateSectionFromQuery() {
    const section = getQueryParam("section");
    const [actualSection, highlightSection] = resolveSection(section || DEFAULT_SECTION);

    // On mobile, no section selected shows the menu list instead of a section.
    settingsPage.classList.toggle("section-selected", Boolean(section));

    // Show the relevant section.
    const activeSection = revealSection(actualSection);

    // Ensure the sidebar has the relevant section highlighted.
    updateSidebarActiveLink(highlightSection);

    // Special cases: maybe scroll to/from "Cancel";
    // maybe activate Linked Accounts tab.
    maybeScrollIntoView(highlightSection, lastHighlighted);
    maybeActivateLinkedAccountsTab(highlightSection);
    scrollToHash(activeSection);

    // Save the lastHighlighted section, so that next time they change sections,
    // we know whether we should scroll.
    lastHighlighted = highlightSection;

    if (actualSection === "billing") {
      fetchBillingHistory();
    }
  }

  // Listen for sidebar menu click event
  if (sidebarMenu) {
    sidebarMenu.addEventListener("click", (event) => {
      if (event.target.tagName === "A") {
        event.preventDefault();
        const href = event.target.getAttribute("href");
        const section = new URLSearchParams(href.substring(href.indexOf("?"))).get("section");
        updateURL(section);
        updateSectionFromQuery();
      }
    });
  }

  // Mobile "back" link: clear ?section so the menu list shows again.
  document.getElementById("mobile-settings-back-link").addEventListener("click", (event) => {
    event.preventDefault();
    const url = new URL(window.location);
    url.searchParams.delete("section");
    history.pushState(null, "", url.toString());
    updateSectionFromQuery();
  });

  updateSectionFromQuery();
  window.addEventListener("popstate", updateSectionFromQuery);
}

function setupApiKeyToggle() {
  const apiKeyButton = document.getElementById("show-api-key");
  const apiKey = document.getElementById("api-key");
  if (apiKeyButton && apiKey) {
    apiKeyButton.addEventListener("click", () => {
      apiKey.classList.toggle("is-hidden");
    });
  }
}

onViewLoad("users#edit", () => {
  setupSectionNavigation();
  setupApiKeyToggle();
  initBillingHistory();
});
